import React, { useState, useEffect } from 'react';
import ProductCard from '../ProductCard/ProductCard';
import { useProducts } from '../../hooks/useProducts';
import { useCart } from '../../contexts/CartContext';
import { useFilters } from '../../contexts/FilterContext';
import { categoryService } from '../../services/api';
import styles from './ProductsSection.module.css';

const ProductsSection = () => {
  const [sort, setSort] = useState('default');
  const [currentPage, setCurrentPage] = useState(1);
  const { categoryFilter, priceRange, clearFilters } = useFilters();
  const [selectedCategoryName, setSelectedCategoryName] = useState('');

  const { addToCart, isInCart, getProductQuantity } = useCart();

  // Charger le nom de la catégorie sélectionnée
  useEffect(() => {
    const loadCategoryName = async () => {
      if (categoryFilter) {
        try {
          const response = await categoryService.getCategory(categoryFilter);
          if (response.success && response.data) {
            setSelectedCategoryName(response.data.name);
          }
        } catch (error) {
          console.error('Erreur lors du chargement de la catégorie:', error);
        }
      } else {
        setSelectedCategoryName('');
      }
    };
    loadCategoryName();
  }, [categoryFilter]);

  // Réinitialiser la page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, priceRange.min, priceRange.max, sort]);

  // Paramètres pour l'API
  const apiParams = {
    sort_by: sort,
    category_id: categoryFilter || undefined,
    min_price: priceRange.min || undefined,
    max_price: priceRange.max || undefined,
    per_page: 12,
    page: currentPage,
  };

  const { products, pagination, isLoading, error } = useProducts(apiParams);

  // Fonction pour gérer l'ajout au panier
  const handleAddToCart = async (productId) => {
    const result = await addToCart(productId, 1);
    if (result.success) {
      // Optionnel: afficher une notification de succès
      console.log('Produit ajouté au panier avec succès');
    } else {
      // Optionnel: afficher une notification d'erreur
      console.error('Erreur:', result.error);
    }
    return result; // IMPORTANT: Retourner le résultat pour ProductCard
  };

  if (isLoading) {
    return (
      <section className={styles.productsSection}>
        <div className={styles.loading}>
          <p>Chargement des produits...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={styles.productsSection}>
        <div className={styles.error}>
          <p>Erreur lors du chargement des produits: {error}</p>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.productsSection}>
      <div className={styles.productsHeader}>
        <h2>Produits Agricoles</h2>
        {(categoryFilter || priceRange.max) && (
          <div className={styles.activeFilters}>
            {categoryFilter && selectedCategoryName && (
              <span className={styles.filterTag}>
                Catégorie: {selectedCategoryName}
                <button
                  onClick={() => clearFilters()}
                  className={styles.clearFilterBtn}
                  title="Effacer les filtres"
                >
                  ×
                </button>
              </span>
            )}
            {priceRange.max && (
              <span className={styles.filterTag}>
                Prix max: {parseFloat(priceRange.max).toLocaleString('fr-FR')} FCFA
                <button
                  onClick={() => clearFilters()}
                  className={styles.clearFilterBtn}
                  title="Effacer les filtres"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        )}
        <div className={styles.filters}>
          <div className={styles.sorting}>
            <span>Tri:</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="default">Par défaut</option>
              <option value="price_asc">Prix croissant</option>
              <option value="price_desc">Prix décroissant</option>
              <option value="name">Nom</option>
              <option value="rating">Mieux notés</option>
              <option value="popular">Plus populaires</option>
            </select>
          </div>
        </div>
      </div>

      <div className={styles.productsGrid}>
        {products.length > 0 ? (
          products.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              onAddToCart={handleAddToCart}
              isInCart={isInCart(product.id)}
              quantityInCart={getProductQuantity(product.id)}
            />
          ))
        ) : (
          <div className={styles.noProducts}>
            <p>Aucun produit trouvé.</p>
          </div>
        )}
      </div>

      {pagination && pagination.last_page > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.paginationButton}
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Précédent
          </button>

          <div className={styles.paginationInfo}>
            Page {pagination.current_page} sur {pagination.last_page}
            {pagination.total && (
              <span className={styles.paginationTotal}>
                ({pagination.total} produit{pagination.total > 1 ? 's' : ''})
              </span>
            )}
          </div>

          <button
            className={styles.paginationButton}
            onClick={() => setCurrentPage(prev => Math.min(pagination.last_page, prev + 1))}
            disabled={currentPage === pagination.last_page}
          >
            Suivant
          </button>
        </div>
      )}
    </section>
  );
};

export default ProductsSection;